import toast from 'react-hot-toast';
import { ParentBlockSitterService } from 'us-web-services';

import { translate } from '../hooks/useSharedTranslation';

interface BlockableSitter {
  id?: number;
  relationship?: { blocked?: boolean | { id?: number } | null; blockedId?: number | null };
}

// Parent-blocks-sitter relationships (SitterDTO.relationship) serialize
// `blocked` as the block record itself (or null) rather than a boolean, so
// the id has to come from `blocked.id`. `blockedId` is read as a fallback
// for locally-synthesized state after a toggle (see the onDone callers).
const resolveBlockedId = (relationship: BlockableSitter['relationship']): number | undefined => {
  const blocked = relationship?.blocked;
  if (typeof blocked === 'object' && blocked !== null) return blocked.id;
  return relationship?.blockedId ?? undefined;
};

// `onDone` receives the new block record's id (or undefined once
// unblocked) so callers can update local state or refetch, however each
// page needs to.
export const toggleSitterBlock = async (
  sitter: BlockableSitter,
  parentId: number,
  onDone: (newBlockedId: number | undefined) => void | Promise<void>,
): Promise<void> => {
  const wasBlocked = !!sitter.relationship?.blocked;
  const blockedId = resolveBlockedId(sitter.relationship);
  try {
    if (wasBlocked && blockedId) {
      await ParentBlockSitterService.delete(blockedId);
      await onDone(undefined);
    } else {
      if (sitter.id == null) return;
      const resp = await ParentBlockSitterService.create({ parentId, sitterId: sitter.id });
      await onDone((resp.data?.data as { id?: number } | undefined)?.id);
    }
    toast.success(translate(wasBlocked ? 'common.caregiverShown' : 'common.caregiverHidden'));
  } catch {
    toast.error(translate('common.toggleCaregiverVisibilityError'));
  }
};
